import {
    DS_BREAKPOINT,
    // eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
    DS_COLOR,
    DS_HEIGHT,
    PRIMITIVES,
} from '@calm/evexia-design-tokens';
import { CardContainer, CardProps } from '@rhg/components/card';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import {
    Box,
    Stack,
    SxProps,
    Theme,
    ThemeProvider,
    useTheme,
} from '@rhg/libs/material';
import {
    MaskOrientation,
    styleMaskedContainer,
    sxAppend,
} from '@rhg/libs/material/sx';
import { ForwardedRef, forwardRef, useMemo, useState } from 'react';
import CardContent from '../card/card-content';
import CardImage from '../card/card-image';
import { BlurredBackgroundImage } from '../ui/blurred-background-image';
import { BANNER_V2_HEIGHT, BANNER_V2_MAX_WIDTH } from './types';

interface BannerCardProps extends Omit<CardProps, 'imageAlt'> {
    buttonGroupSx?: SxProps<Theme>;
    skipHorizontalContentAdjustments?: boolean;
}

const styleActualGridWrapper = {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    position: 'relative',
    backgroundColor: (theme: Theme) => ({
        xs: 'unset',
        md:
            theme.palette.mode === 'light'
                ? DS_COLOR.WHITE_100P
                : DS_COLOR.LIGHT_TEXT_BODY, // LIGHT_TEXT_BODY matches dark linear gradient color}
    }),
};
const styleContentWrapper = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexGrow: 1,
};
const styleContent = {
    position: 'relative',
    maxWidth: DS_BREAKPOINT.MEDIUM,
    width: '100%',
    flex: 1,
    px: (theme: Theme) => ({
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
};
const styleCardContent = {
    zIndex: 2,
};
const styleHorizontalContainer = {
    maxHeight: DS_HEIGHT.HORIZONTAL_CARD_SIZE,
    display: 'flex',
    flexDirection: 'row-reverse',
    flexWrap: 'nowrap',
};

const styleBannerImage = {
    width: '100%',
    minHeight: '100%',
    maxHeight: BANNER_V2_HEIGHT,
};
const styleHorizontalImage = {
    width: DS_HEIGHT.HORIZONTAL_CARD_SIZE,
};
const styleCardContainer = {
    width: '100%',
    maxWidth: BANNER_V2_MAX_WIDTH,
    height: '100%',
    '& .MuiPaper-root': { maxHeight: BANNER_V2_HEIGHT },
};
const styleHorizontalContent = {
    alignItems: 'center',
    '& > div:first-of-type': {
        alignSelf: 'start',
        width: '100%',
    },
};
const styleMaskedImageContainer = {
    width: '100%',
    height: BANNER_V2_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
};

export const BannerCard = forwardRef<HTMLDivElement, BannerCardProps>(
    function BannerCard(
        {
            title,
            subtitle,
            content,
            category,
            progress,
            button,
            image,
            buttonGroupSx,
            backgroundColor,
            isEntireCardClickable,
            entireCardButtonProps,
            onClick,
            label,
            trackButton,
            sx,
            isNotClickable,
            role,
            skipHorizontalContentAdjustments = false,
            'aria-label': ariaLabel,
            'data-syntheticid': dataSyntheticId,
        }: BannerCardProps,
        ref: ForwardedRef<HTMLDivElement>
    ) {
        const currentBreakpoint = useCurrentBreakpointName();
        const theme = useTheme();
        const lightTheme = useProvidedTheme('light');
        const darkTheme = useProvidedTheme('dark');
        const [hasFocus, setHasFocus] = useState(false);

        const clickHandlers = useMemo(
            () => ({
                onMouseEnter: () => setHasFocus(true),
                onMouseLeave: () => setHasFocus(false),
                onFocus: () => setHasFocus(true),
                onBlur: () => setHasFocus(false),
                onClick: onClick,
            }),
            [onClick]
        );

        // TODO: switch this to be based on average image color
        // ref: https://codepen.io/AtomicForce/pen/nVgamy
        const isDarkImageGradiant = theme.palette.mode === 'dark';
        const showAsHorizontal = ['sm', 'xs'].includes(currentBreakpoint);
        const styleForcedSize = image?.forcedSize
            ? { width: image?.forcedSize }
            : undefined;
        const show3by2Ratio = ['xl', 'lg'].includes(currentBreakpoint);

        if (showAsHorizontal) {
            return (
                <Stack
                    ref={ref}
                    gap={4}
                    alignItems="center"
                    sx={{ width: '100%' }}
                >
                    <CardContainer
                        clickHandlers={clickHandlers}
                        role={role}
                        ariaLabel={ariaLabel}
                        isNotClickable={isNotClickable}
                        entireCardButtonProps={entireCardButtonProps}
                        isEntireCardClickable={isEntireCardClickable}
                        sx={sxAppend(styleCardContainer, sx)}
                        backgroundColor={backgroundColor}
                        trackButton={trackButton}
                        data-cy="bannerCard"
                        data-syntheticid={dataSyntheticId}
                    >
                        <Box
                            sx={sxAppend(
                                styleActualGridWrapper,
                                styleHorizontalContainer
                            )}
                            style={{ backgroundColor }}
                        >
                            <Box sx={styleContentWrapper}>
                                <ThemeProvider
                                    theme={
                                        isDarkImageGradiant
                                            ? darkTheme
                                            : lightTheme
                                    }
                                >
                                    <CardContent
                                        title={title.text}
                                        titleComponent={title.component}
                                        titleMaxLines={title.maxLines ?? 2}
                                        titleBottomSpacing={title.bottomSpacing}
                                        subtitle={subtitle?.text}
                                        subtitleVariant={subtitle?.variant}
                                        subtitleMaxLines={subtitle?.maxLines}
                                        tag={content?.tag}
                                        action={content?.action}
                                        categoryTitleColor={
                                            category?.titleColor
                                        }
                                        progress={progress?.value}
                                        progressText={progress?.text}
                                        progressTextAlt={progress?.textAlt}
                                        sx={sxAppend(
                                            content?.sx,
                                            button?.additionalAction
                                                ? styleHorizontalContent
                                                : {}
                                        )}
                                        additionalAction={
                                            button?.additionalAction
                                        }
                                        buttonTopSpacing={button?.topSpacing}
                                        categoryTitle={category?.title}
                                        categoryIcon={category?.icon}
                                        titleTopSpacing={0}
                                        variant="horizontal"
                                        skipHorizontalContentAdjustments={
                                            skipHorizontalContentAdjustments
                                        }
                                    />
                                </ThemeProvider>
                            </Box>
                            <Box>
                                <CardImage
                                    src={image?.src}
                                    alt=""
                                    ratio={show3by2Ratio ? '3:2' : '1:1'}
                                    sx={sxAppend(
                                        styleForcedSize,
                                        styleHorizontalImage
                                    )}
                                    bezel={PRIMITIVES.radius._12}
                                    imageSideRadiusEnabled={false}
                                    decorations={image?.decorations}
                                    iconFocus={image?.focusIcon}
                                    isFocused={hasFocus}
                                    showIconsOnFocused={
                                        image?.showIconsOnFocused
                                    }
                                    aria-label={
                                        onClick && label ? label : undefined
                                    }
                                    trackButton={trackButton}
                                    {...image?.props}
                                    {...(isNotClickable || isEntireCardClickable
                                        ? {}
                                        : clickHandlers)}
                                />
                            </Box>
                        </Box>
                    </CardContainer>
                    {button?.group}
                </Stack>
            );
        }

        return (
            <MaxColumnsContainer ref={ref} columns={12}>
                <CardContainer
                    clickHandlers={clickHandlers}
                    role={role}
                    ariaLabel={ariaLabel}
                    isNotClickable={isNotClickable}
                    entireCardButtonProps={entireCardButtonProps}
                    isEntireCardClickable={isEntireCardClickable}
                    sx={sxAppend(styleCardContainer, sx)}
                    backgroundColor={backgroundColor}
                    trackButton={trackButton}
                    data-cy="bannerCard"
                    data-syntheticid={dataSyntheticId}
                >
                    <BlurredBackgroundImage
                        orientation={MaskOrientation.Left}
                        src={image?.src}
                        sx={sxAppend(styleActualGridWrapper, {
                            backgroundColor,
                        })}
                    >
                        <Box sx={sxAppend(styleContentWrapper, styleContent)}>
                            <ThemeProvider
                                theme={
                                    isDarkImageGradiant ? darkTheme : lightTheme
                                }
                            >
                                <CardContent
                                    title={title.text}
                                    titleComponent={title.component}
                                    titleMaxLines={title.maxLines ?? 2}
                                    titleBottomSpacing={title.bottomSpacing}
                                    subtitle={subtitle?.text}
                                    subtitleVariant={subtitle?.variant}
                                    subtitleMaxLines={subtitle?.maxLines}
                                    tag={content?.tag}
                                    action={content?.action}
                                    categoryTitleColor={category?.titleColor}
                                    progress={progress?.value}
                                    progressText={progress?.text}
                                    progressTextAlt={progress?.textAlt}
                                    sx={sxAppend(styleCardContent, content?.sx)}
                                    additionalAction={button?.additionalAction}
                                    buttonTopSpacing={button?.topSpacing}
                                    buttonGroup={button?.group}
                                    variant="bannerV2"
                                    icon={title.icon}
                                    titleTopSpacing={title.topSpacing}
                                    categoryTitle={category?.title}
                                    categoryDescription={category?.description}
                                    categoryIcon={category?.icon}
                                    buttonGroupSx={buttonGroupSx}
                                />
                            </ThemeProvider>
                        </Box>
                        <Box
                            sx={sxAppend(
                                styleMaskedContainer(MaskOrientation.Right),
                                styleMaskedImageContainer
                            )}
                        >
                            <CardImage
                                imageSideRadiusEnabled={false}
                                src={image?.src}
                                ratio={show3by2Ratio ? '3:2' : '1:1'}
                                fillContainer
                                sx={styleBannerImage}
                                alt=""
                            />
                        </Box>
                    </BlurredBackgroundImage>
                </CardContainer>
            </MaxColumnsContainer>
        );
    }
);
